/**
 * Simple Error Boundary pour éviter les problèmes de TDZ et capturer les exceptions via Sentry.
 */
import React from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<any>;
  onError?: (error: Error) => void;
}

function ErrorBoundary({ children, fallback, onError }: ErrorBoundaryProps) {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const errorId = React.useRef('ERR_' + Date.now());

  React.useEffect(() => {
    if (hasError) {
      setHasError(false);
      setError(null);
    }
  }, [children]);

  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      const caughtError = event.error || new Error(event.message);
      setError(caughtError);
      
      // Capture l'erreur globale via Sentry
      Sentry.captureException(caughtError);
      
      if (onError) onError(caughtError);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [onError]);

  if (hasError) {
    if (fallback && error) {
      return React.createElement(fallback, { error, errorId: errorId.current });
    }

    return React.createElement('div', {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#0a192f',
        color: '#ccd6f6',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center'
      }
    }, [
      React.createElement('div', {
        key: 'icon',
        style: { fontSize: '4rem', marginBottom: '1rem' }
      }, '💥'),
      
      React.createElement('h1', {
        key: 'title',
        style: { fontSize: '2rem', color: '#64ffda', marginBottom: '1rem' }
      }, 'Erreur d\'Application'),
      
      React.createElement('p', {
        key: 'message',
        style: { fontSize: '1rem', color: '#8892b0', marginBottom: '2rem' }
      }, 'Une erreur s\'est produite. Veuillez recharger la page.'),
      
      React.createElement('button', {
        key: 'reload',
        onClick: () => window.location.reload(),
        style: {
          padding: '0.75rem 1.5rem',
          backgroundColor: '#64ffda',
          color: '#0a192f',
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '1rem',
          cursor: 'pointer'
        }
      }, 'Recharger')
    ]);
  }

  try {
    return children;
  } catch (err) {
    const errorObj = err as Error;
    setHasError(true);
    setError(errorObj);
    
    // Capture l'erreur de rendu via Sentry
    Sentry.captureException(errorObj);
    
    if (onError) onError(errorObj);
    return null;
  }
}

export default ErrorBoundary;

export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);
  
  React.useEffect(() => {
    if (error) {
      // Capture l'erreur manuelle via Sentry
      Sentry.captureException(error);
      throw error;
    }
  }, [error]);

  return {
    captureError: setError,
    resetError: () => setError(null)
  };
}

export function ErrorBoundaryWithRetry({ 
  children, 
  maxRetries = 3 
}: { 
  children: React.ReactNode; 
  maxRetries?: number; 
}) {
  const [retryCount, setRetryCount] = React.useState(0);
  const [key, setKey] = React.useState(0);

  const handleError = React.useCallback(() => {
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount(c => c + 1);
        setKey(k => k + 1);
      }, 1000);
    }
  }, [retryCount, maxRetries]);

  return React.createElement(ErrorBoundary, {
    key,
    onError: handleError,
    children
  });
}
